import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

export const useCombinedRefs = (ref, internalRef) => {
	return useMemo(
		() => instance => {
			internalRef.current = instance;
			if (!ref) return;
			if (typeof ref === 'function') {
				ref(instance);
			} else {
				ref.current = instance;
			}
		},
		[ref],
	);
};

export const useDebounce = (value, delay) => {
	const [debounce, setDebounce] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => setDebounce(value), delay);
		return () => clearTimeout(handler);
	}, [value, delay]);

	return debounce;
};

export const useDelay = (method, listeners = []) => {
	const [active, setActive] = useState(false);

	useEffect(() => {
		setActive(false);
		setTimeout(() => {
			method();
			setActive(true);
		}, listeners[0]);
	}, [...listeners]);

	return active;
};

export const useFocus = (listeners = []) => {
	const ref = useRef(null);

	useEffect(() => {
		ref.current?.focus();
	}, [...listeners]);

	return ref;
};

export const useInterval = (method, listeners = []) => {
	useEffect(() => {
		setTimeout(() => method(), listeners[0]);
	}, [...listeners]);
};

export const useLoaded = (method = null, listeners = []) => {
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		loaded && method ? method() : setLoaded(true);
	}, [loaded, ...listeners]);

	return loaded;
};

export const useMention = (comment, listeners = []) => {
	useEffect(() => {
		const card = document.getElementById(`comment_${comment.id}`);
		const mentions = card?.querySelectorAll('.mention') || [];
		mentions.forEach(anchor => {
			const a = document.createElement('a');
			a.innerHTML = anchor.innerHTML;
			anchor.getAttributeNames().forEach(attrName => {
				const attrValue = anchor.getAttribute(attrName);
				if (attrName === 'data-href') {
					a.setAttribute('href', attrValue);
					a.setAttribute('target', '_blank');
				}
				a.setAttribute(attrName, attrValue);
			});
			anchor.parentNode.replaceChild(a, anchor);
		});
	}, [...listeners]);
};

export const useMounted = (method, listeners = []) => {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		mounted && method ? method() : setMounted(true);
	}, [...listeners]);

	return mounted;
};

export const useRouteChange = (method, listeners = []) => {
	const router = useRouter();

	useEffect(() => {
		router.events.on('routeChangeStart', method);
		return () => router.events.off('routeChangeStart', method);
	}, [...listeners]);
};
